import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Building2 } from 'lucide-react';
import TenantOverviewTab from '@/components/admin/tenant-detail/TenantOverviewTab';
import TenantAssetsTab from '@/components/admin/tenant-detail/TenantAssetsTab';
import TenantBotsTab from '@/components/admin/tenant-detail/TenantBotsTab';
import TenantReportsTab from '@/components/admin/tenant-detail/TenantReportsTab';

export interface Tenant {
  id: string;
  name: string;
  industry: string | null;
  status: string | null;
  primary_domain: string | null;
  client_contact_email: string | null;
  developer_email: string | null;
  website_url?: string | null;
  install_scope?: string | null;
  summary_forward_email?: string | null;
  specialisations?: any;
  do_not_mention?: string | null;
  brand_colors?: any;
  created_at: string | null;
}

const statusColors: Record<string, string> = {
  onboarding: 'bg-blue-500/10 text-blue-500',
  intake: 'bg-sky-500/10 text-sky-500',
  building: 'bg-yellow-500/10 text-yellow-500',
  preview: 'bg-purple-500/10 text-purple-500',
  live: 'bg-green-500/10 text-green-500',
  paused: 'bg-gray-500/10 text-gray-500',
};

const AdminTenantDetail: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  
  const activeTab = searchParams.get('tab') || 'overview';

  useEffect(() => {
    const fetchTenantData = async () => {
      if (!tenantId) return;

      try {
        // Try advisory_tenants first (newer system)
        const { data: advisoryData, error: advisoryError } = await supabase
          .from('advisory_tenants')
          .select('*')
          .eq('id', tenantId)
          .single();

        if (!advisoryError && advisoryData) {
          // Map advisory_tenants fields to Tenant interface
          setTenant({
            id: advisoryData.id,
            name: advisoryData.name,
            industry: advisoryData.industry,
            status: advisoryData.status,
            primary_domain: advisoryData.website_url,
            client_contact_email: advisoryData.owner_email,
            developer_email: advisoryData.developer_email,
            website_url: advisoryData.website_url,
            install_scope: advisoryData.install_scope,
            summary_forward_email: advisoryData.summary_forward_email,
            specialisations: advisoryData.specialisations,
            do_not_mention: advisoryData.do_not_mention,
            brand_colors: advisoryData.brand_colors,
            created_at: advisoryData.created_at,
          });
        } else {
          // Fallback to legacy tenants table
          const { data: legacyData, error: legacyError } = await supabase
            .from('tenants')
            .select('*')
            .eq('id', tenantId)
            .single();

          if (legacyError) throw legacyError;
          setTenant(legacyData);
        }
      } catch (error) {
        console.error('Error fetching tenant:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [tenantId]);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AdminLayout>
    );
  }

  if (!tenant) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Tenant not found</h3>
          <Link to="/admin/tenants" className="mt-4 inline-flex items-center text-primary hover:underline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to tenants
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/admin/tenants">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-foreground">{tenant.name}</h1>
              <Badge 
                variant="secondary"
                className={statusColors[tenant.status || 'onboarding']}
              >
                {tenant.status || 'onboarding'}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1 capitalize">
              {tenant.industry || 'No industry specified'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="assets">Assets</TabsTrigger>
            <TabsTrigger value="bots">Bots</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <TenantOverviewTab tenant={tenant} />
          </TabsContent>

          <TabsContent value="assets" className="mt-6">
            <TenantAssetsTab tenantId={tenant.id} />
          </TabsContent>

          <TabsContent value="bots" className="mt-6">
            <TenantBotsTab tenantId={tenant.id} tenantIndustry={tenant.industry} />
          </TabsContent>

          <TabsContent value="reports" className="mt-6">
            <TenantReportsTab tenantId={tenant.id} tenantName={tenant.name} />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminTenantDetail;
